import { useEffect, useState, useCallback } from "react";
import { Cookie, Settings, ShieldCheck, BarChart3, Megaphone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
} from "@/components/ui/dialog";

const STORAGE_KEY = "rp_cookie_consent_v2";

// GDPR cookie categories.
// "necessary" is always required and cannot be disabled.
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

const CATEGORIES = [
  {
    key: "necessary",
    icon: ShieldCheck,
    title: "Strictly necessary",
    required: true,
    description:
      "Required for the site to function — load balancing, security, your form submissions and your cookie choices. These cannot be disabled.",
  },
  {
    key: "analytics",
    icon: BarChart3,
    title: "Analytics",
    description:
      "Anonymous usage stats so we can understand which pages help customers most. We never sell or share this data.",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Marketing",
    description:
      "Cookies set by advertising partners (Google Ads, Trustpilot) to measure ad effectiveness and to show you Repair Point ads on other sites.",
  },
  {
    key: "preferences",
    icon: Sparkles,
    title: "Preferences",
    description:
      "Remembers your choices, such as which articles you have read, so we can personalise the experience on your next visit.",
  },
];

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.consent) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        consent,
        version: 2,
        at: new Date().toISOString(),
      })
    );
    // Expose globally so analytics scripts can read it
    window.__rp_cookie_consent = consent;
    window.dispatchEvent(new CustomEvent("rp:cookie-consent-changed", { detail: consent }));
  } catch {}
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_CONSENT);

  // Initial mount — show banner if no prior consent
  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      window.__rp_cookie_consent = existing.consent;
      return;
    }
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Listen for footer "Cookie settings" link
  useEffect(() => {
    const onOpen = () => {
      const existing = loadConsent();
      setDraft(existing?.consent || DEFAULT_CONSENT);
      setOpen(true);
      setShowPrefs(true);
    };
    window.addEventListener("rp:open-cookie-preferences", onOpen);
    return () => window.removeEventListener("rp:open-cookie-preferences", onOpen);
  }, []);

  const acceptAll = useCallback(() => {
    const all = { necessary: true, analytics: true, marketing: true, preferences: true };
    saveConsent(all);
    setOpen(false);
    setShowPrefs(false);
  }, []);

  const rejectAll = useCallback(() => {
    const none = { ...DEFAULT_CONSENT };
    saveConsent(none);
    setOpen(false);
    setShowPrefs(false);
  }, []);

  const saveCustom = useCallback(() => {
    saveConsent({ ...draft, necessary: true });
    setOpen(false);
    setShowPrefs(false);
  }, [draft]);

  // Note: deliberately NO close-without-choice option — GDPR requires an active
  // choice and does not allow "implied consent" via dismissal.
  if (!open) return null;

  return (
    <>
      {/* Bottom banner (level 1) */}
      {!showPrefs && (
        <div
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
          aria-modal="false"
          className="fixed bottom-0 inset-x-0 z-[70] px-4 pb-4 md:px-6 md:pb-6 animate-in slide-in-from-bottom-4 duration-500"
        >
          <div className="max-w-5xl mx-auto bg-card border border-border rounded-md shadow-2xl">
            <div className="flex flex-col gap-4 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold text-base leading-snug">
                    We respect your privacy.
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1.5 leading-relaxed">
                    We use strictly necessary cookies to make the site work. With your consent, we may
                    also use analytics, marketing and preference cookies to improve our service. You
                    can accept all, reject all, or choose which categories to allow. Read our{" "}
                    <a href="/cookies" className="text-primary underline font-medium">Cookie Policy</a>{" "}
                    and{" "}
                    <a href="/privacy" className="text-primary underline font-medium">Privacy Policy</a>.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-3 sm:justify-end">
                <Button
                  data-testid="cookie-reject"
                  variant="outline"
                  onClick={rejectAll}
                  className="h-10 px-5 text-sm font-semibold border-border hover:bg-secondary"
                >
                  Reject all
                </Button>
                <Button
                  data-testid="cookie-customize"
                  variant="outline"
                  onClick={() => { setDraft(loadConsent()?.consent || DEFAULT_CONSENT); setShowPrefs(true); }}
                  className="h-10 px-5 text-sm font-semibold border-border hover:bg-secondary inline-flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" /> Customise
                </Button>
                <Button
                  data-testid="cookie-accept"
                  onClick={acceptAll}
                  className="h-10 px-5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Granular preferences (level 2) — only opens when explicit user request */}
      <Dialog open={showPrefs} onOpenChange={(v) => !v && setShowPrefs(false)}>
        <DialogContent
          data-testid="cookie-preferences"
          className="sm:max-w-lg max-h-[85vh] overflow-y-auto p-0"
        >
          <DialogHeader className="px-6 pt-6 md:px-7 md:pt-7">
            <DialogTitle className="font-heading text-xl md:text-2xl font-bold tracking-tight">
              Cookie preferences
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              Choose which categories of cookies you allow. You can change these at any time from the
              &ldquo;Cookie settings&rdquo; link in the footer.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-2 md:px-7 space-y-4 mt-4">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.key}
                  data-testid={`cookie-cat-${c.key}`}
                  className="border border-border rounded-md p-4 flex items-start gap-4"
                >
                  <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-heading font-semibold text-sm leading-tight">{c.title}</p>
                      <Switch
                        data-testid={`cookie-switch-${c.key}`}
                        checked={c.required ? true : Boolean(draft[c.key])}
                        disabled={c.required}
                        onCheckedChange={(v) =>
                          setDraft((d) => ({ ...d, [c.key]: Boolean(v) }))
                        }
                        aria-label={`Toggle ${c.title} cookies`}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{c.description}</p>
                    {c.required && (
                      <p className="text-[11px] text-primary mt-1.5 font-medium">Always active</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-6 py-5 md:px-7 md:py-6 mt-2 border-t border-border bg-secondary flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
            <Button
              data-testid="cookie-pref-reject"
              variant="outline"
              onClick={rejectAll}
              className="h-10 px-5 text-sm font-semibold bg-card border-border"
            >
              Reject all
            </Button>
            <Button
              data-testid="cookie-pref-save"
              variant="outline"
              onClick={saveCustom}
              className="h-10 px-5 text-sm font-semibold bg-card border-border"
            >
              Save preferences
            </Button>
            <Button
              data-testid="cookie-pref-accept"
              onClick={acceptAll}
              className="h-10 px-5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Accept all
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
